import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "../i18n";

const MANIFESTO_IMG =
  "https://images.unsplash.com/photo-1584247793672-567c535a5e44?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export const Manifesto = () => {
  const ref = useRef(null);
  const { t } = useLang();
  const chapters = t("manifesto.chapters");
  const stats = t("manifesto.stats");
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <section id="hakkimizda" data-testid="manifesto-section" className="relative pt-24 md:pt-32 pb-10 md:pb-12 scroll-mt-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center gap-3 mb-6">
          <span className="h-px w-10 bg-[#2F4A26]" />
          <p className="font-sans uppercase tracking-[0.3em] text-xs text-[#2F4A26]">{t("manifesto.label")}</p>
        </div>
        <h2 className="font-serif text-4xl md:text-6xl tracking-tighter text-[#1A1715] leading-none max-w-3xl mb-16 md:mb-20">
          {t("manifesto.headingPre")}<span className="italic text-[#2F4A26]">{t("manifesto.headingItalic")}</span>{t("manifesto.headingPost")}
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          <div ref={ref} className="relative overflow-hidden aspect-[3/4] rounded-xl ring-1 ring-black/10">
            <motion.img
              style={{ y, scale: 1.2 }}
              src={MANIFESTO_IMG}
              alt="Mimari ahşap yapı"
              className="w-full h-full object-cover will-change-transform"
            />
          </div>

          <div className="lg:pl-8">
            {chapters.map((c, i) => (
              <motion.div
                key={c.num}
                data-testid={`chapter-${c.num}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="border-t border-black/10 py-8 first:border-t-0 first:pt-0"
              >
                <div className="flex gap-6">
                  <span className="font-serif text-xl text-[#2F4A26] shrink-0">{c.num}</span>
                  <div>
                    <h3 className="font-serif text-2xl md:text-3xl text-[#1A1715] mb-3">{c.title}</h3>
                    <p className="text-[#6B6560] leading-relaxed">{c.body}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-14 md:mt-16">
          {stats.map((s, i) => (
            <motion.div
              key={i}
              data-testid={`stat-${i}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="rounded-xl bg-white ring-1 ring-black/10 px-8 py-8"
            >
              <span className="block h-px w-8 bg-[#2F4A26] mb-4" />
              <div className={`font-serif text-[#1A1715] tracking-tight mb-2 ${s.value.length > 6 ? "text-4xl md:text-[2.6rem] lg:text-5xl whitespace-nowrap" : "text-5xl md:text-6xl"}`}>{s.value}</div>
              <div className="font-sans uppercase tracking-[0.15em] text-xs text-[#6B6560]">{s.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
